import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '~/lib/api';

interface GmailMessage {
  id: string;
  from: string;
  subject: string;
  date: string;
  snippet: string;
  has_attachments: boolean;
  attachment_count: number;
  already_imported: boolean;
}

export default function TbitInvoicesGmailPickerPage() {
  const [messages, setMessages] = useState<GmailMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('has:attachment newer_than:7d');
  const [importing, setImporting] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchMessages = async (q: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/tbit-invoices/api/gmail/messages?q=${encodeURIComponent(q)}&limit=30`);
      if (!res.ok) {
        const data = await res.json();
        if (data.error === 'gmail_disconnected') {
          setError('Gmail desconectado. Reconecta tu cuenta desde el panel.');
          return;
        }
        throw new Error(data.error || 'Error al cargar emails');
      }
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages(query);
  }, []);

  const handleImport = async (messageId: string) => {
    setImporting(messageId);
    try {
      const res = await apiFetch('/tbit-invoices/api/gmail/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message_id: messageId }),
      });
      if (!res.ok) throw new Error('Error al importar');
      const data = await res.json();
      navigate(`/tbit-invoices/records/${data.id}`);
    } catch (err: any) {
      setError(err.message);
      setImporting(null);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchMessages(query);
  };

  const presetQueries = [
    { label: 'Con adjuntos (7 dias)', q: 'has:attachment newer_than:7d' },
    { label: 'Con adjuntos (30 dias)', q: 'has:attachment newer_than:30d' },
    { label: 'PDFs recientes', q: 'has:attachment filename:pdf newer_than:14d' },
    { label: 'Facturas', q: 'subject:(factura OR invoice) newer_than:30d' },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Importar desde Gmail</h1>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <input
          type="text"
          className="input input-bordered flex-1"
          placeholder="Buscar en Gmail..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? <span className="loading loading-spinner loading-sm" /> : 'Buscar'}
        </button>
      </form>

      {/* Preset filters */}
      <div className="flex flex-wrap gap-2">
        {presetQueries.map((p) => (
          <button
            key={p.q}
            className={`btn btn-xs ${query === p.q ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { setQuery(p.q); fetchMessages(p.q); }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      {/* Email list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="card bg-base-100 shadow animate-pulse">
              <div className="card-body py-3 space-y-2">
                <div className="h-4 bg-base-300 rounded w-48" />
                <div className="h-3 bg-base-300 rounded w-96" />
              </div>
            </div>
          ))}
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-10 text-base-content/50">
          No se encontraron emails con esos filtros.
        </div>
      ) : (
        <div className="space-y-2">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`card bg-base-100 shadow hover:shadow-md transition-shadow ${msg.already_imported ? 'opacity-60' : ''}`}
            >
              <div className="card-body py-3 flex-row items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm truncate max-w-[250px]">{msg.from}</span>
                    <span className="text-xs text-base-content/40 whitespace-nowrap">
                      {new Date(msg.date).toLocaleDateString('es-CO')}
                    </span>
                    {msg.has_attachments && (
                      <span className="badge badge-xs badge-outline">
                        {msg.attachment_count} adjunto{msg.attachment_count > 1 ? 's' : ''}
                      </span>
                    )}
                    {msg.already_imported && (
                      <span className="badge badge-xs badge-ghost">ya importado</span>
                    )}
                  </div>
                  <p className="text-sm truncate">{msg.subject}</p>
                  <p className="text-xs text-base-content/40 truncate">{msg.snippet}</p>
                </div>
                <button
                  className="btn btn-sm btn-primary shrink-0"
                  disabled={importing === msg.id}
                  onClick={() => handleImport(msg.id)}
                >
                  {importing === msg.id ? (
                    <span className="loading loading-spinner loading-xs" />
                  ) : msg.already_imported ? (
                    'Ver'
                  ) : (
                    'Importar'
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
